import React, { useState, useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const AdvancedMetrics = ({ metricsData }) => {
  const [analysisType, setAnalysisType] = useState("conversion");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  // Format date for display
  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split("-");
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  // Check if we have the necessary data
  const hasRequiredData = useMemo(() => {
    return (
      metricsData?.clicks?.length > 0 &&
      metricsData?.impressions?.length > 0 &&
      metricsData?.plays?.length > 0
    );
  }, [metricsData]);

  // Get all available dates from the data
  const availableDates = useMemo(() => {
    if (!hasRequiredData) return [];

    // Collect dates from all data sources
    const allDates = new Set([
      ...(metricsData.clicks || []).map((item) => new Date(item.date)),
      ...(metricsData.impressions || []).map((item) => new Date(item.date)),
      ...(metricsData.plays || []).map((item) => new Date(item.date)),
    ]);

    // Convert to array and sort
    return [...allDates].sort((a, b) => a.getTime() - b.getTime());
  }, [metricsData, hasRequiredData]);

  // Process raw data for all dates
  const processedData = useMemo(() => {
    if (!hasRequiredData) return null;

    // Get all unique dates from all data sources
    const allDates = new Set([
      ...(metricsData.clicks || []).map((item) => item.date),
      ...(metricsData.impressions || []).map((item) => item.date),
      ...(metricsData.plays || []).map((item) => item.date),
    ]);

    // Convert to array and sort chronologically
    let sortedDates = [...allDates].sort((a, b) => new Date(a) - new Date(b));

    // Apply date filtering if date range is selected
    if (startDate && endDate) {
      sortedDates = sortedDates.filter((dateStr) => {
        const date = new Date(dateStr);
        return date >= startDate && date <= endDate;
      });
    }

    // Create a map for each date with metrics
    const dateMap = new Map();

    // Initialize with empty data for each date
    sortedDates.forEach((date) => {
      dateMap.set(date, {
        date,
        formattedDate: formatDate(date),
        clicks: 0,
        impressions: 0,
        plays: 0,
        ctr: 0,
        ctp: 0,
      });
    });

    // Fill in the data
    if (metricsData.clicks) {
      metricsData.clicks.forEach((item) => {
        if (dateMap.has(item.date)) {
          dateMap.get(item.date).clicks = item.value;
        }
      });
    }

    if (metricsData.impressions) {
      metricsData.impressions.forEach((item) => {
        if (dateMap.has(item.date)) {
          dateMap.get(item.date).impressions = item.value;
        }
      });
    }

    if (metricsData.plays) {
      metricsData.plays.forEach((item) => {
        if (dateMap.has(item.date)) {
          dateMap.get(item.date).plays = item.value;
        }
      });
    }

    // Calculate derived metrics
    dateMap.forEach((data) => {
      // CTR: clicks / impressions
      data.ctr =
        data.impressions > 0
          ? ((data.clicks / data.impressions) * 100).toFixed(2)
          : 0;

      // CTP: plays / clicks
      data.ctp =
        data.clicks > 0 ? ((data.plays / data.clicks) * 100).toFixed(2) : 0;
    });

    return {
      dates: sortedDates,
      data: [...dateMap.values()],
    };
  }, [metricsData, hasRequiredData, startDate, endDate]);

  // Calculate trends and insights
  const insights = useMemo(() => {
    if (!processedData || processedData.data.length < 2) return null;

    const data = processedData.data;
    const latestData = data[data.length - 1];
    const previousData = data[data.length - 2];

    // Calculate period totals
    const totalImpressions = data.reduce(
      (sum, item) => sum + item.impressions,
      0
    );
    const totalClicks = data.reduce((sum, item) => sum + item.clicks, 0);
    const totalPlays = data.reduce((sum, item) => sum + item.plays, 0);

    // Calculate totals
    const totalCTR =
      totalImpressions > 0
        ? ((totalClicks / totalImpressions) * 100).toFixed(2)
        : 0;
    const totalCTP =
      totalClicks > 0 ? ((totalPlays / totalClicks) * 100).toFixed(2) : 0;

    // Calculate day-over-day changes
    const ctrChange =
      ((parseFloat(latestData.ctr) - parseFloat(previousData.ctr)) /
        parseFloat(previousData.ctr)) *
      100;
    const ctpChange =
      ((parseFloat(latestData.ctp) - parseFloat(previousData.ctp)) /
        parseFloat(previousData.ctp)) *
      100;

    return {
      summary: {
        totalImpressions,
        totalClicks,
        totalPlays,
        totalCTR,
        totalCTP,
      },
      changes: {
        ctr: ctrChange.toFixed(1),
        ctp: ctpChange.toFixed(1),
      },
    };
  }, [processedData]);

  // Generate chart data based on selected analysis type
  const chartData = useMemo(() => {
    if (!processedData) return null;

    const { data } = processedData;
    const labels = data.map((item) => item.formattedDate);

    if (analysisType === "conversion") {
      return {
        labels,
        datasets: [
          {
            label: "Calc CTR (%)",
            data: data.map((item) => parseFloat(item.ctr)),
            borderColor: "rgba(54, 162, 235, 1)",
            backgroundColor: "rgba(54, 162, 235, 0.1)",
            tension: 0.4,
            fill: false,
            yAxisID: "y",
          },
          {
            label: "CTP (%)",
            data: data.map((item) => parseFloat(item.ctp)),
            borderColor: "rgba(255, 99, 132, 1)",
            backgroundColor: "rgba(255, 99, 132, 0.1)",
            tension: 0.4,
            fill: false,
            yAxisID: "y",
          },
        ],
      };
    } else if (analysisType === "volume") {
      return {
        labels,
        datasets: [
          {
            type: "line",
            label: "Impressions",
            data: data.map((item) => item.impressions),
            borderColor: "rgba(54, 162, 235, 1)",
            backgroundColor: "rgba(54, 162, 235, 0.1)",
            tension: 0.4,
            yAxisID: "y",
          },
          {
            type: "bar",
            label: "Clicks",
            data: data.map((item) => item.clicks),
            backgroundColor: "rgba(255, 99, 132, 0.7)",
            yAxisID: "y1",
          },
          {
            type: "bar",
            label: "Plays",
            data: data.map((item) => item.plays),
            backgroundColor: "rgba(75, 192, 192, 0.7)",
            yAxisID: "y1",
          },
        ],
      };
    }

    return null;
  }, [processedData, analysisType]);

  // Generate appropriate chart options based on analysis type
  const chartOptions = useMemo(() => {
    if (!chartData) return null;

    const commonOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          labels: {
            color: "#E5E7EB",
            font: {
              size: 12,
            },
            usePointStyle: true,
            padding: 15,
          },
        },
        tooltip: {
          backgroundColor: "#374151",
          titleColor: "#E5E7EB",
          bodyColor: "#E5E7EB",
          padding: 12,
          cornerRadius: 4,
        },
      },
      scales: {
        x: {
          ticks: {
            color: "#E5E7EB",
          },
          grid: {
            color: "rgba(229, 231, 235, 0.1)",
          },
        },
      },
    };

    if (analysisType === "conversion") {
      return {
        ...commonOptions,
        plugins: {
          ...commonOptions.plugins,
          title: {
            display: true,
            text: "Conversion Rates Over Time",
            color: "#E5E7EB",
            font: {
              size: 16,
              weight: "bold",
            },
          },
        },
        scales: {
          ...commonOptions.scales,
          y: {
            type: "linear",
            display: true,
            position: "left",
            title: {
              display: true,
              text: "Percentage (%)",
              color: "#E5E7EB",
            },
            ticks: {
              color: "#E5E7EB",
            },
            grid: {
              color: "rgba(229, 231, 235, 0.1)",
            },
          },
        },
      };
    } else if (analysisType === "volume") {
      return {
        ...commonOptions,
        plugins: {
          ...commonOptions.plugins,
          title: {
            display: true,
            text: "Traffic Volume Analysis",
            color: "#E5E7EB",
            font: {
              size: 16,
              weight: "bold",
            },
          },
        },
        scales: {
          ...commonOptions.scales,
          y: {
            type: "linear",
            display: true,
            position: "left",
            title: {
              display: true,
              text: "Impressions",
              color: "#E5E7EB",
            },
            ticks: {
              color: "#E5E7EB",
              callback: (value) =>
                value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value,
            },
            grid: {
              color: "rgba(229, 231, 235, 0.1)",
            },
          },
          y1: {
            type: "linear",
            display: true,
            position: "right",
            title: {
              display: true,
              text: "Clicks & Plays",
              color: "#E5E7EB",
            },
            ticks: {
              color: "#E5E7EB",
              callback: (value) =>
                value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value,
            },
            grid: {
              drawOnChartArea: false,
            },
          },
        },
      };
    }

    return null;
  }, [chartData, analysisType]);

  // Add the date filter function for DatePicker
  const isDateAvailable = (date) => {
    return availableDates.some(
      (availableDate) => availableDate.toDateString() === date.toDateString()
    );
  };

  // Reset date range handler
  const resetDateRange = () => {
    setDateRange([null, null]);
  };

  if (!hasRequiredData) {
    return (
      <div className="bg-gray-800 rounded-lg p-8 text-center">
        <div className="text-gray-400">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <h3 className="text-lg font-medium">
            Insufficient Data for Advanced Metrics
          </h3>
          <p className="mt-2">
            Please upload clicks, impressions, and plays data to view advanced
            metrics.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-200">
            Advanced Analytics
          </h2>
          <p className="text-sm text-gray-400">
            In-depth analysis and insights from your metrics
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setAnalysisType("conversion")}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              analysisType === "conversion"
                ? "bg-blue-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            Conversion Rates
          </button>
          <button
            onClick={() => setAnalysisType("volume")}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              analysisType === "volume"
                ? "bg-blue-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            Traffic Volume
          </button>
        </div>
      </div>

      <div className="bg-gray-800 p-4 rounded-lg">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Date Range
            </label>
            <div className="flex items-center gap-2">
              <DatePicker
                selected={startDate}
                onChange={(date) => setDateRange([date, endDate])}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                placeholderText="Start Date"
                dateFormat="MM/dd/yyyy"
                filterDate={isDateAvailable}
                className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500 w-32"
                calendarClassName="bg-gray-800 border border-gray-700 text-white"
                dayClassName={(date) =>
                  isDateAvailable(date)
                    ? "text-white hover:bg-blue-600"
                    : "text-gray-500 cursor-not-allowed"
                }
              />
              <span className="text-gray-400">to</span>
              <DatePicker
                selected={endDate}
                onChange={(date) => setDateRange([startDate, date])}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                placeholderText="End Date"
                dateFormat="MM/dd/yyyy"
                filterDate={isDateAvailable}
                className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500 w-32"
                calendarClassName="bg-gray-800 border border-gray-700 text-white"
                dayClassName={(date) =>
                  isDateAvailable(date)
                    ? "text-white hover:bg-blue-600"
                    : "text-gray-500 cursor-not-allowed"
                }
              />
              {(startDate || endDate) && (
                <button
                  onClick={resetDateRange}
                  className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-md text-sm"
                  title="Reset date range"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {startDate && endDate && (
            <div className="text-sm text-gray-400">
              Showing data from {startDate.toLocaleDateString()} to{" "}
              {endDate.toLocaleDateString()}
            </div>
          )}
        </div>
      </div>

      {insights && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-lg p-4 shadow-md">
            <h3 className="text-gray-400 text-sm font-medium mb-1">
              Total Impressions
            </h3>
            <p className="text-2xl font-bold text-gray-200">
              {insights.summary.totalImpressions.toLocaleString()}
            </p>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 shadow-md">
            <h3 className="text-gray-400 text-sm font-medium mb-1">
              Total Clicks
            </h3>
            <p className="text-2xl font-bold text-gray-200">
              {insights.summary.totalClicks.toLocaleString()}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              Total Calc CTR: {insights.summary.totalCTR}%
            </p>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 shadow-md">
            <h3 className="text-gray-400 text-sm font-medium mb-1">
              Total Plays
            </h3>
            <p className="text-2xl font-bold text-gray-200">
              {insights.summary.totalPlays.toLocaleString()}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              Total CTP: {insights.summary.totalCTP}%
            </p>
          </div>
        </div>
      )}

      <div className="bg-gray-800 p-6 rounded-lg">
        {chartData && chartOptions ? (
          <div className="h-96">
            <Line data={chartData} options={chartOptions} />
          </div>
        ) : (
          <div className="py-12 text-center text-gray-400">
            No data available for the selected analysis.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedMetrics;
